import React, { useState } from 'react';
import FriendFilterDropdown from './FriendFilterDropdown';
import { isParty, filterOneToOneExpenses } from '../../utils/expenseUtils';

function ExpensesList({ expenses, users, groups = [], currentUser, onDeleteExpense, onEditExpense }) {
  const [friendFilter, setFriendFilter] = useState('');
  const getFriendName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name : 'Unknown';
  };

  const getGroupName = (groupId) => {
    if (!groupId || !groups) return null;
    const g = groups.find((x) => x.id === groupId);
    return g ? g.name : null;
  };

  const canEdit = (expense) => {
    if (expense.groupId) {
      const g = groups.find((x) => x.id === expense.groupId);
      if (!g) return false;
      const member = g.members?.find((m) => m.userId === currentUser.id);
      return member?.role === 'admin' || member?.role === 'co-admin' || g.createdBy === currentUser.id;
    } else {
      return expense.paidByUserId === currentUser.id;
    }
  };

  const getCategoryEmoji = (desc) => {
    const d = desc.toLowerCase();
    if (d.includes('food') || d.includes('dinner') || d.includes('lunch') || d.includes('eat') || d.includes('drink')) return '🍔';
    if (d.includes('travel') || d.includes('cab') || d.includes('flight') || d.includes('uber') || d.includes('fuel')) return '🚗';
    if (d.includes('rent') || d.includes('flat') || d.includes('stay') || d.includes('hotel')) return '🏠';
    if (d.includes('bill') || d.includes('electricity') || d.includes('wifi') || d.includes('water')) return '💡';
    if (d.includes('settle') || d.includes('payment') || d.includes('paid')) return '🤝';
    return '📦';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const visibleExpenses = friendFilter ? filterOneToOneExpenses(expenses, currentUser.id, friendFilter) : expenses;

  return (
    <div className="card-box container-large">
      <div className="container-header">
        <h3>🧾 Expense Log</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <FriendFilterDropdown users={users} currentUserId={currentUser.id} value={friendFilter} onChange={setFriendFilter} />
          <span className="count-badge">{visibleExpenses.length} total</span>
        </div>
      </div>

      {visibleExpenses.length === 0 ? (
        <div className="empty-state">
          <p>{friendFilter ? `No expenses shared with ${getFriendName(friendFilter)} yet.` : 'No expenses logged in this account.'}</p>
        </div>
      ) : (
        <div className="expenses-table">
          <div className="table-header-row">
            <span>Details</span>
            <span>Paid By</span>
            <span>Total Amount</span>
            <span>Participants Split</span>
            <span>Actions</span>
          </div>
          <div className="table-body">
            {visibleExpenses.map((expense) => (
              <div key={expense.id} className="table-row">
                <div className="table-cell-desc" data-label="Details">
                  <span className="cell-emoji">{getCategoryEmoji(expense.description)}</span>
                  <div className="cell-text">
                    <span className="main-text">
                      {expense.description}
                      {getGroupName(expense.groupId) && (
                        <span className="group-badge-inline">{getGroupName(expense.groupId)}</span>
                      )}
                    </span>
                    <span className="sub-text">{formatDate(expense.createdAt)}</span>
                  </div>
                </div>
                <div className="table-cell" data-label="Paid By">
                  <span className="payer-badge">{getFriendName(expense.paidByUserId)}</span>
                </div>
                <div className="table-cell val-bold" data-label="Total Amount">₹{expense.amount.toFixed(2)}</div>
                <div className="table-cell split-details" data-label="Participants Split">
                  {expense.participants?.map((p) => (
                    <div key={p.userId} className="split-detail-pill">
                      <span>{getFriendName(p.userId)}</span>
                      <span>₹{p.amountOwed.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="table-cell text-right" data-label="Actions">
                  {canEdit(expense) && (
                    <button className="btn btn-secondary btn-sm" style={{ marginRight: '8px' }} onClick={() => onEditExpense(expense)}>
                      Edit
                    </button>
                  )}
                  {isParty(expense, currentUser.id) ? (
                    <button className="btn btn-danger btn-sm" onClick={() => onDeleteExpense(expense.id)}>
                      Delete
                    </button>
                  ) : (
                    <span title="You can only delete expenses you paid or participated in.">🔒</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpensesList;
